import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { Alert, CircularProgress, Divider } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useMutation } from '@tanstack/react-query'
import DataApi from 'api/faber/DataApi'
import { RoundedButton } from 'common/Button/Button'
import NormalText from 'common/Typography/NormalText'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'

import { TransformedFaberAndGlobalUsersType } from '../otpackComponents/UserProfileV3'

interface Props {
  handleRefreshNetworkDialogClose: () => void
  faberToken: string
  refreshNetworkDialogOpen: boolean
  customerRow: SubscriptionsTableRows | TransformedFaberAndGlobalUsersType
}

export default function RefreshNetworkDialog({
  faberToken,
  refreshNetworkDialogOpen,
  handleRefreshNetworkDialogClose,
  customerRow
}: Props) {
  /* Function to refresh network */
  const { mutate: refreshNetwork, isLoading: isRefreshNetworkLoading } =
    useMutation(DataApi.refreshNetwork, {
      onSuccess: () => {
        toast.success('Network has been refreshed for the customer')
      },
      onError: () => {
        toast.error(
          `Error in refreshing network. Please check customer SIM's status`
        )
      },
      onSettled: () => {
        handleRefreshNetworkDialogClose()
      }
    })

  const userId = customerRow.userID

  let iccid: string

  if ('userID' in customerRow) {
    iccid = (customerRow as TransformedFaberAndGlobalUsersType).iccid
  } else if ('_id' in customerRow) {
    iccid = (customerRow as SubscriptionsTableRows).iccid
  }
  const handleConfirm = () => {
    refreshNetwork({
      userId: userId ? userId : '',
      faberToken: faberToken,
      iccid: iccid || ''
    })
  }
  return (
    <Dialog
      open={refreshNetworkDialogOpen}
      onClose={handleRefreshNetworkDialogClose}
    >
      <DialogTitle id='alert-dialog-title'>{'Refresh Network'}</DialogTitle>
      <Divider sx={{ width: '100%' }} />
      <DialogContent>
        <NormalText sx={{ p: 0 }}>
          If the customer has data left, eSim enabled, and is connected to the
          right network but is still facing connectivity issues, click confirm
          to refresh their network
        </NormalText>
        <Alert severity='info' sx={{ mt: '1rem' }}>
          Upon successful network refresh, please inform customer to restart
          their phone and help ensure that they are connected to the right
          network. <br />
          <strong>Wait about 5 minutes </strong> to see if the issue is resolved
          before escalating (if needed).
        </Alert>
      </DialogContent>
      <DialogActions sx={{ mb: '1rem' }}>
        <RoundedButton onClick={handleRefreshNetworkDialogClose}>
          Cancel
        </RoundedButton>
        <LoadingButton
          loading={isRefreshNetworkLoading}
          variant='filled'
          onClick={handleConfirm}
          loadingIndicator={<CircularProgress color='inherit' size={16} />}
          autoFocus
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
